import {
  ComponentKey,
  ConfiguratorPV,
  getAttributeValue,
  ICfgCompositeVariant,
  IComponentProductData,
  IComponentProductKey,
  IComponentProductTextData,
  ICompositeProduct,
  IPrice,
} from '@canvas-logic/clp-domain';
import { createUrl } from './utils';
import FileSaver from 'file-saver';
import { Sdk } from '@/types';
import { API } from '@canvas-logic/clp-viewer3d';
import QRCode from 'qrcode';
import { IPdfConfigDto, IPdfDto, IPdfSummary, IPdfSummaryItem } from '@canvas-logic/clp-api-types';
import i18next from '../../i18n';
import { ICompositeProductApi } from '@canvas-logic/clp-sdk';

type TranslationObject = Record<string, string>;
const pdfDtoBase: Partial<IPdfDto> = {
  modelImage: '',
  logo: '',
  title: '',
  description: '',
  link: '',
  qrCode: '',
  totalPrice: '',
  summaryItems: {
    titleAttributes: [],
    items: [
      {
        name: '',
        description: '',
        article: '',
        amount: '',
        price: '',
      },
      {
        name: '',
        description: '',
        article: '',
        amount: '',
        price: '',
      },
    ],
  },
  address: '',
  email: '',
  phone: '',
  translations: {},
};

export class PdfService {
  private readonly CURRENCIES = ['USD', 'EUR'];
  private readonly QR_CODE_WIDTH = 200;
  private readonly PDF_API_URL = window.APP_CONFIG.SERVER_URL + '/configurator/pdf/';
  private readonly TEMPLATE_URL = createUrl('assets/templates/pdf');
  private readonly t: (key: string) => string;
  private pdfConfig: IPdfConfigDto | null = null;
  private compositeName = '';
  private readonly catalogId: number;
  private readonly draft: boolean;

  constructor(
    private readonly sdk: Sdk,
    private readonly api3d: API,
    { catalogId, draft }: { catalogId: number; draft: boolean },
  ) {
    this.catalogId = catalogId;
    this.draft = draft;
    this.t = (key: string) => i18next.t(key, { ns: 'pdf' });
  }

  public async downloadPdf(link: string): Promise<void> {
    try {
      await this.downloadPdfConfig();
      const dto = await this.createPdfDto(link);
      const pdfBlob = await this.generatePdf(dto);
      await this.savePdfFile(pdfBlob);
    } catch (error) {
      console.error('Error during PDF generation:', error);
      throw error;
    }
  }

  private async createPdfDto(link: string): Promise<IPdfDto> {
    if (!this.sdk.composite) {
      throw new Error('No composite product found.');
    }
    if (!this.pdfConfig) {
      throw new Error(`No pdf config provided`);
    }

    const compositeApi = this.sdk.api;
    const [price, modelImage, name, variants, metaIds, product, productFullData] = await Promise.all([
      compositeApi.getPrice(),
      this.api3d.createScreenshot(2190 / 2, 2300 / 2),
      compositeApi.getName(),
      compositeApi.getVariant(),
      compositeApi.getMetaIds(),
      compositeApi.getProduct(),
      compositeApi.getProductData(),
    ]);
    const productData = compositeApi.getComponentsData();
    if (!productData) {
      throw new Error('No product data found.');
    }

    this.compositeName = productFullData.texts.name;
    const qrCode = await this.generateQRCode(link, { width: this.QR_CODE_WIDTH });
    const totalPrice = this.formatTotalPrice(price);
    const summaryItems = this.transformDataToSummaryItems(compositeApi, variants, productData);

    const titleAttributes = [];
    const attributeConfig = this.pdfConfig.titleAttributes.find(
      (it) => it.productTypeKey === product.productType.productTypeKey,
    );
    if (attributeConfig) {
      for (const it of attributeConfig.attributes) {
        const component = variants[it.componentKey];
        const name = it.name || `${it.componentKey}.${it.attributeKey}`;

        const translationKey = this.summaryAttributeMap[name];
        const translatedName = translationKey ? this.t(translationKey) : name;

        titleAttributes.push({
          name: translatedName,
          value: getAttributeValue(component[it.attributeKey]),
        });
      }
    }

    const pdfSummary: IPdfSummary = {
      titleAttributes,
      items: summaryItems,
    };

    const translations: TranslationObject = this.getPdfTranslations({ idType: product.catalogMeta.metaId });

    return {
      ...pdfDtoBase,
      modelImage,
      qrCode,
      link,
      title: this.compositeName,
      description: this.pdfConfig.showCompositeProductDescription ? productFullData.texts.description : '',
      shortDescription: this.pdfConfig.showCompositeProductShortDescription
        ? productFullData.texts.shortDescription
        : '',
      totalPrice: this.pdfConfig.showPrices ? totalPrice : '',
      summaryItems: pdfSummary,
      logo: this.pdfConfig.logo ?? undefined,
      address: this.pdfConfig.contactInfo.address || '',
      phone: this.pdfConfig.contactInfo.phone || '',
      email: this.pdfConfig.contactInfo.email || '',
      translations,
      config: this.pdfConfig,
    };
  }

  private async generatePdf(pdfDto: IPdfDto): Promise<Blob> {
    const response = await fetch(this.PDF_API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        templateUrl: this.TEMPLATE_URL,
        clearCache: true,
        model: pdfDto,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to generate PDF: ${response.statusText}`);
    }

    return response.blob();
  }

  private async savePdfFile(pdfBlob: Blob): Promise<void> {
    const fileName = this.generateFileName();
    FileSaver.saveAs(pdfBlob, fileName);
  }

  private transformDataToSummaryItems(
    compositeApi: ICompositeProductApi,
    variants: IPrice | ICfgCompositeVariant | ICompositeProduct,
    productData: Record<
      string,
      | (IComponentProductKey & {
          texts: IComponentProductTextData;
          images: { url: string }[];
        })
      | null
    >,
  ): IPdfSummaryItem[] {
    const items: IPdfSummaryItem[] = [];

    for (const [componentKey, variant] of Object.entries(variants)) {
      if (!compositeApi.isComponentInPdf(componentKey)) {
        continue;
      }
      const v = new ConfiguratorPV(variant);
      if (v.isNullish) {
        continue;
      }
      const data = productData[componentKey];

      const price =
        this.pdfConfig?.showPrices && !v.isNullish && v.meta
          ? this.CURRENCIES.reduce(
              (acc, currency) => acc || (v.meta.price[currency] ? `${v.meta.price[currency]} ${currency}` : ''),
              '',
            )
          : '';

      const amount = compositeApi.getComponentPartsAmount(componentKey);
      if (amount > 0) {
        items.push({
          view: '',
          image: this.pdfConfig?.showImage ? data?.images[0]?.url : undefined,
          name: data?.texts.name ?? '',
          description: this.pdfConfig?.showProductDescription ? data?.texts.description : '',
          shortDescription: this.pdfConfig?.showProductShortDescription ? data?.texts.shortDescription : '',
          article: v.isNullish ? '' : v?.meta.id,
          amount: amount.toString(),
          price,
        });
      }
    }
    return groupItems(items, getPdfSummaryItemKey);
  }

  private formatTotalPrice(price: IPrice): string {
    const [currency, amount] = Object.entries(price).at(0) ?? [];
    return currency && amount ? `${amount} ${currency}` : '';
  }

  private generateFileName(): string {
    const now = new Date();
    const dateString = new Intl.DateTimeFormat('en-GB').format(now).replace(/[\/]/g, '.');
    const timeString = `${now.getHours()}_${now.getMinutes()}_${now.getSeconds()}`;
    return `${this.compositeName}_${dateString}_${timeString}.pdf`;
  }

  private async generateQRCode(link: string, options: Record<string, unknown>): Promise<string> {
    try {
      return await QRCode.toDataURL(link, options);
    } catch (error) {
      console.error('QR code generation error:', error);
      throw error;
    }
  }

  private async downloadPdfConfig() {
    if (!this.pdfConfig) {
      const url = `${window.APP_CONFIG.SERVER_URL}/configurator/pdf-config?catalogId=${this.catalogId}&version=${
        this.draft ? 'draft' : 'active'
      }`;
      const response = await fetch(url);
      this.pdfConfig = await response.json();
    }
  }

  private getPdfTranslations({ idType }: Partial<{ idType: string }> = {}) {
    return {
      price_disclaimer: this.t('first_page.price_disclaimer'),
      link_info_text: this.t('first_page.link_info.your_configuration_text'),
      link_info_link: this.t('first_page.link_info.your_configuration_link'),
      link_info_qr: this.t('first_page.link_info.or_scan_qr'),
      summary_title: this.t('summary_page.title'),
      summary_color_attribute: this.t('summary_page.attributes.color'),
      summary_columns_product: this.t('summary_page.columns.product'),
      summary_columns_article: idType ?? this.t('summary_page.columns.article'),
      summary_columns_amount: this.t('summary_page.columns.amount'),
      summary_columns_price: this.t('summary_page.columns.price'),
      footer_contact_us: this.t('summary_page.footer.contact_us'),
    };
  }

  private summaryAttributeMap: Record<string, string> = {
    Color: 'summary_page.attributes.color',
  };

  private groupItems(items: IPdfSummaryItem[]): IPdfSummaryItem[] {
    return [];
  }
}

function getPdfSummaryItemKey(item: IPdfSummaryItem): string {
  const result: string[] = [];
  for (const [key, value] of Object.entries(item)) {
    if (key === 'amount') {
      continue;
    }
    result.push(value?.toString() ?? '');
  }
  return result.join(':=:');
}
function groupItems(items: IPdfSummaryItem[], keyFn: (item: IPdfSummaryItem) => string): IPdfSummaryItem[] {
  const groups = new Map<string, IPdfSummaryItem>();
  for (const item of items) {
    const key = keyFn(item);
    const groupedItem = groups.get(key);
    if (!groupedItem) {
      groups.set(key, item);
    } else {
      groupedItem.amount = (+groupedItem.amount + +item.amount).toString();
    }
  }

  const result = Array.from(groups.values());
  return result;
}
